<template>
  <section class="hero" :style="heroStyle">
    <div class="hero-content">
      <h1 class="hero-title">{{ title }}</h1>
      <p v-if="subtitle" class="hero-subtitle">{{ subtitle }}</p>
      <div v-if="showActions" class="hero-actions">
        <slot name="actions">
          <router-link to="/explore" class="btn btn-primary btn-lg">
            Start Exploring
          </router-link>
          <router-link to="/culture" class="btn btn-secondary btn-lg">
            Discover Culture
          </router-link>
        </slot>
      </div>
    </div>
    <div class="hero-scroll-indicator" v-if="showScrollIndicator">
      <svg class="scroll-icon" viewBox="0 0 24 24" fill="none">
        <path d="M7 13L12 18L17 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
  </section>
</template>

<script>
export default {
  name: 'Hero',
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    imageUrl: {
      type: String,
      required: true,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    showScrollIndicator: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: '60vh',
    },
    overlay: {
      type: String,
      default: 'default', // 'default', 'light', 'dark', 'none'
    },
  },
  computed: {
    heroStyle() {
      return {
        backgroundImage: `url(${this.imageUrl})`,
        height: this.height,
      };
    },
    overlayClass() {
      return `hero-overlay-${this.overlay}`;
    },
  },
};
</script>

<style scoped>
.hero {
  position: relative;
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-height: 400px;
}

.hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(196, 30, 58, 0.8) 0%, rgba(196, 30, 58, 0.4) 100%);
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: var(--neutral-100);
  max-width: 900px;
  padding: 0 var(--space-4);
  animation: heroFadeIn 1s ease-out;
}

.hero-title {
  font-family: var(--font-display);
  font-size: var(--text-5xl);
  font-weight: 700;
  margin-bottom: var(--space-6);
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.7);
  line-height: var(--leading-tight);
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: var(--text-xl);
  font-weight: 400;
  opacity: 0.95;
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-8);
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
}

.hero-actions {
  display: flex;
  justify-content: center;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.hero-scroll-indicator {
  position: absolute;
  bottom: var(--space-8);
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  color: var(--neutral-100);
  animation: bounce 2s infinite;
}

.scroll-icon {
  width: 24px;
  height: 24px;
}

/* Hero Overlay Variants */
.hero.hero-overlay-light::before {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0.2) 100%);
}

.hero.hero-overlay-dark::before {
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 100%);
}

.hero.hero-overlay-none::before {
  display: none;
}

/* Animations */
@keyframes heroFadeIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateX(-50%) translateY(0);
  }
  40% {
    transform: translateX(-50%) translateY(-10px);
  }
  60% {
    transform: translateX(-50%) translateY(-5px);
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .hero-title {
    font-size: var(--text-4xl);
  }
  
  .hero-subtitle {
    font-size: var(--text-lg);
  }
}

@media (max-width: 768px) {
  .hero {
    background-attachment: scroll;
    min-height: 300px;
  }
  
  .hero-title {
    font-size: var(--text-3xl);
    margin-bottom: var(--space-4);
  }
  
  .hero-subtitle {
    font-size: var(--text-base);
    margin-bottom: var(--space-6);
  }
  
  .hero-actions {
    flex-direction: column;
    align-items: center;
    gap: var(--space-3);
  }
  
  .hero-actions .btn {
    width: 100%;
    max-width: 280px;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: var(--text-2xl);
  }
  
  .hero-subtitle {
    font-size: var(--text-sm);
  }
}
</style>